import { atom } from 'jotai'
import { atomWithStorage, createJSONStorage, RESET } from 'jotai/utils'
import { decrypt, encrypt } from '../encryption'

// Create a custom storage object with proper typing
const createCustomStorage = <T>() => {
  const inMemoryStorage = new Map<string, string>()

  return createJSONStorage<T>(() => ({
    getItem: (key: string): string | null => {
      try {
        return localStorage.getItem(key)
      } catch {
        return inMemoryStorage.get(key) ?? null
      }
    },
    setItem: (key: string, newValue: string) => {
      try {
        localStorage.setItem(key, newValue)
      } catch {
        inMemoryStorage.set(key, newValue)
      }
    },
    removeItem: (key: string) => {
      try {
        localStorage.removeItem(key)
      } catch {
        inMemoryStorage.delete(key)
      }
    },
  }))
}

// Create a custom encrypted storage object
const createEncryptedStorage = <T>() => {
  const inMemoryStorage = new Map<string, string>()

  return createJSONStorage<T>(() => ({
    getItem: (key: string): string | null => {
      try {
        const encryptedValue = localStorage.getItem(key)
        if (!encryptedValue) return null
        const decrypted = decrypt(encryptedValue)
        // If decryption fails (returns empty string) and we had a value, 
        // it might be old unencrypted data or corrupted. 
        // If it's empty string, createJSONStorage might fail parsing if it expects JSON.
        // But createJSONStorage handles parsing errors usually? 
        // No, we should return null if decryption fails effectively, so it falls back to initial value.
        if (!decrypted && encryptedValue) return null; 
        return decrypted
      } catch {
        const encryptedValue = inMemoryStorage.get(key)
        if (!encryptedValue) return null
        return decrypt(encryptedValue) || null
      }
    },
    setItem: (key: string, newValue: string) => {
      try {
        const encryptedValue = encrypt(newValue)
        localStorage.setItem(key, encryptedValue)
      } catch {
        const encryptedValue = encrypt(newValue)
        inMemoryStorage.set(key, encryptedValue)
      }
    },
    removeItem: (key: string) => {
      try {
        localStorage.removeItem(key)
      } catch {
        inMemoryStorage.delete(key)
      }
    },
  }))
}

// Utility function to create atoms with safe storage and generic type support
export function createAtomWithSafeStorage<T>(key: string, initialValue: T) {
  const customStorage = createCustomStorage<T>()
  const baseAtom = atomWithStorage<T>(key, initialValue, customStorage)
  
  const safeAtom = atom(
    (get) => {
      try {
        return get(baseAtom)
      } catch (error) {
        // console.error(`Error reading atom ${key}:`, error)
        return initialValue
      }
    },
    (_get, set, update: T | typeof RESET | ((prev: T) => T)) => {
      try {
        set(baseAtom, update)
      } catch (error) {
        // console.error(`Error updating atom ${key}:`, error)
      }
    }
  )

  return safeAtom
}

export function createAtomWithEncryptedStorage<T>(key: string, initialValue: T) {
  const customStorage = createEncryptedStorage<T>()
  const baseAtom = atomWithStorage<T>(key, initialValue, customStorage)
  
  const safeAtom = atom(
    (get) => {
      try {
        return get(baseAtom)
      } catch (error) {
        // console.error(`Error reading atom ${key}:`, error)
        return initialValue
      }
    },
    (_get, set, update: T | typeof RESET | ((prev: T) => T)) => {
      try {
        set(baseAtom, update)
      } catch (error) {
        // console.error(`Error updating atom ${key}:`, error)
      }
    }
  )

  return safeAtom
}